import { MemberEligibilityInfo } from "@/App/types";
import { determineAccumulationMax, determineMedicalCopays } from "@/App/utils/utils";
import { formatDollars } from "capitalrx-utilities";
import { FC, useMemo } from "react";
import "./UnifiedCard.scss";

const formatDollarsWithoutZeros = (value: string) => {
  const formatted = formatDollars(value);
  return formatted.substring(0, formatted.length - 3);
};

export const UnifiedCardBack: FC<{ readonly memberEligibilityInfo: MemberEligibilityInfo }> = ({ memberEligibilityInfo }) => {
  /**
   * The real way to handle this is to hide the copay when
   * 1. the plan has an in network deductible
   * 2. pcp visits apply to the in network deductible
   *
   * but for now, hardcoded to the JD155 bronze plan
   */
  const hidePcpCopay = useMemo(() => {
    return memberEligibilityInfo?.plan?.client_id === "JD155" && memberEligibilityInfo?.medical_plan_benefit?.benefit_id === "JD155BRONZEHSA";
  }, [memberEligibilityInfo]);

  const pcpCopay = useMemo(() => {
    const copays = determineMedicalCopays(memberEligibilityInfo, "Physician Visit");
    if (copays == null) {
      return "$-";
    }
    const [lowest, highest] = copays;
    if (lowest == null) {
      return "$-";
    } else if (highest == null) {
      return formatDollarsWithoutZeros(lowest.toString());
    } else {
      return `${formatDollarsWithoutZeros(lowest.toString())} - ${formatDollarsWithoutZeros(highest.toString())}`;
    }
  }, [memberEligibilityInfo]);

  const specialistCopay = useMemo(() => {
    const copays = determineMedicalCopays(memberEligibilityInfo, "Specialist Visit");
    if (copays == null) {
      return "$-";
    }
    const [lowest, highest] = copays;
    if (lowest == null) {
      return "$-";
    } else if (highest == null) {
      return formatDollarsWithoutZeros(lowest.toString());
    } else {
      return `${formatDollarsWithoutZeros(lowest.toString())} - ${formatDollarsWithoutZeros(highest.toString())}`;
    }
  }, [memberEligibilityInfo]);

  const singleInNetworkDeductibleMax = useMemo(() => {
    const value = determineAccumulationMax(memberEligibilityInfo, "in_network_tier_1", "deductible", "subscriber_only");
    return value == null ? "$-" : formatDollarsWithoutZeros(value);
  }, [memberEligibilityInfo]);

  const singleInNetworkOopMax = useMemo(() => {
    const value = determineAccumulationMax(memberEligibilityInfo, "in_network_tier_1", "maximum_out_of_pocket", "subscriber_only");
    return value == null ? "$-" : formatDollarsWithoutZeros(value);
  }, [memberEligibilityInfo]);

  const singleOutOfNetworkDeductibleMax = useMemo(() => {
    const value = determineAccumulationMax(memberEligibilityInfo, "out_of_network", "deductible", "subscriber_only");
    return value == null ? "$-" : formatDollarsWithoutZeros(value);
  }, [memberEligibilityInfo]);

  const singleOutOfNetworkOopMax = useMemo(() => {
    const value = determineAccumulationMax(memberEligibilityInfo, "out_of_network", "maximum_out_of_pocket", "subscriber_only");
    return value == null ? "$-" : formatDollarsWithoutZeros(value);
  }, [memberEligibilityInfo]);

  const familyInNetworkDeductibleMax = useMemo(() => {
    const value = determineAccumulationMax(memberEligibilityInfo, "in_network_tier_1", "deductible", "family");
    return value == null ? "$-" : formatDollarsWithoutZeros(value);
  }, [memberEligibilityInfo]);

  const familyInNetworkOopMax = useMemo(() => {
    const value = determineAccumulationMax(memberEligibilityInfo, "in_network_tier_1", "maximum_out_of_pocket", "family");
    return value == null ? "$-" : formatDollarsWithoutZeros(value);
  }, [memberEligibilityInfo]);

  const familyOutOfNetworkDeductibleMax = useMemo(() => {
    const value = determineAccumulationMax(memberEligibilityInfo, "out_of_network", "deductible", "family");
    return value == null ? "$-" : formatDollarsWithoutZeros(value);
  }, [memberEligibilityInfo]);

  const familyOutOfNetworkOopMax = useMemo(() => {
    const value = determineAccumulationMax(memberEligibilityInfo, "out_of_network", "maximum_out_of_pocket", "family");
    return value == null ? "$-" : formatDollarsWithoutZeros(value);
  }, [memberEligibilityInfo]);

  return (
    <div className="card card-back">
      <div className="header">
        <p>Member Responsibility</p>
      </div>

      <div className="content">
        <div className="section">
          <p className="section-title">Copays:</p>
          {!hidePcpCopay && (
            <div>
              <span className="section-subtitle">PCP: </span>
              <span className="section-content">{pcpCopay}</span>
            </div>
          )}
          <div>
            <span className="section-subtitle">Specialist: </span>
            <span className="section-content">{specialistCopay}</span>
          </div>
        </div>

        <div className="section">
          <p className="section-title">Deductible/Out of Pocket Max:</p>
          <div>
            <span className="section-subtitle">In-Network: </span>
            <span className="section-content">
              {singleInNetworkDeductibleMax}/{singleInNetworkOopMax}
            </span>
          </div>
          <div>
            <span className="section-subtitle">Out-of-Network: </span>
            <span className="section-content">
              {singleOutOfNetworkDeductibleMax}/{singleOutOfNetworkOopMax}
            </span>
          </div>
        </div>

        <div className="section">
          <p className="section-title">Family Deductible/Out of Pocket Max:</p>
          <div>
            <span className="section-subtitle">In-Network: </span>
            <span className="section-content">
              {familyInNetworkDeductibleMax}/{familyInNetworkOopMax}
            </span>
          </div>
          <div>
            <span className="section-subtitle">Out-of-Network: </span>
            <span className="section-content">
              {familyOutOfNetworkDeductibleMax}/{familyOutOfNetworkOopMax}
            </span>
          </div>
        </div>
      </div>

      <div className="footer"></div>
    </div>
  );
};
